import { ASSIGNMENT_REFETCH_QUERIES } from "@src/features/contentLibrary/assignmentDialogs/constants";
import {
  CONTENT_LIBRARY_REFETCH_QUERIES,
  LIBRARY_PAGINATED_TABLE_REFETCH_QUERIES,
  TAG_CONTEXT_STATE_REFETCH_QUERIES,
} from "@src/lib/contexts/TagContext/TagContext";
import { GraphqlOperations } from "@src/types.generated";
import type { OpusAgentEventLike } from "./events";
import type { McpMutationName } from "./mcpMutationNames.generated";

// After the Opus Agent writes data, the dashboard pages behind the chat
// refetch what the write made stale: each SUCCEEDED tool-call event names the
// GraphQL mutations it ran (a build_* tool runs several), and this map says
// which queries each one changes. A mutation with no entry refetches nothing.
// The map's type does the checking: its keys must be mutations the agent may
// run (McpMutationName, generated by `yarn codegen` from the schema
// snapshot's @mcp fields) and its values real query operations, so typecheck
// rejects a stale or misspelled key, while a new @mcp mutation needs no entry.
// scadmin's cross-repo check (this repo's codegen against scadmin's schema,
// then typecheck) fails the scadmin PR that drops a mapped mutation.
// Only active queries refetch (refetchQueries' include by name).
//
// The builders' editor queries (a course, module, check-in or Path draft
// open in its builder) are left out on purpose: the builder holds unsaved
// local edits, and a refetch under it would fight its autosave.

const Q = GraphqlOperations.Query;

type QueryName = (typeof GraphqlOperations.Query)[keyof typeof Q];

// A library item's own pages beside the library tables.
const CONTENT_DETAIL = [
  Q.CourseDetailHeader,
  Q.CourseDetailOverview,
  Q.CourseModuleMemberships,
  Q.LibraryItemOverviewCourseContext,
  Q.CoursesPageHeader,
  Q.ResourcesPageHeader,
  Q.ModuleManageSettingsTab_Module,
  Q.SkillDetailOverview,
  Q.AdminRecentlyEditedModules,
];

const CONTENT = [...CONTENT_LIBRARY_REFETCH_QUERIES, ...CONTENT_DETAIL];

const FOLDERS = [
  ...LIBRARY_PAGINATED_TABLE_REFETCH_QUERIES,
  Q.FoldersGrid_Folders,
  Q.FolderLibraryItem,
];

const SHARING = [
  Q.CourseSharingPermissions,
  Q.ModuleSharingPermissions,
  Q.TrainingResourceSharingPermissions,
  Q.ShareSkillDialog_Skill,
  Q.LibraryItemContentPermissions,
];

const LIBRARY_ACCESS = [
  ...ASSIGNMENT_REFETCH_QUERIES,
  Q.CourseDetailLibraryAccess,
  Q.SkillDetailLibraryAccess,
  Q.ScormCourseDetailLibraryAccess,
  Q.LibraryItemAccessAutomation,
  Q.AutomationList,
  Q.AutomationListCount,
];

const PEOPLE = [
  Q.GetPeople,
  Q.UsersPageContent,
  Q.UsersPageHeader,
  Q.UserPageHeaderEmployee,
  Q.UserPageContentEmployee,
  Q.UserDetailsAdminEmployee,
  Q.UserDetailProfileTabAdminEmployee,
  Q.InactiveUsersCount,
  Q.InactiveUsersSheet,
  Q.UsersNoRoleSheet,
  Q.UsersNoRoleWidgetCount,
  Q.AdminEmployeesCount,
  Q.TotalActiveUsersWidget,
  Q.RoleDetail,
  Q.LocationDetail,
  Q.LocationGroupPagePeople,
];

const LOCATIONS = [
  Q.GetLocations,
  Q.LocationsTable_Locations,
  Q.LocationGroupLocationsTable_Locations,
  Q.LocationDetail,
  Q.LocationsPageHeader,
  Q.TotalLocationsCount,
];

const LOCATION_GROUPS = [
  ...LOCATIONS,
  Q.GetLocationGroups,
  Q.PaginatedLocationGroupsTable,
  Q.LocationGroupDetail,
  Q.SubRegionsList_LocationGroup,
];

const ROLES = [
  Q.GetRoles,
  Q.GetRolesForSelect,
  Q.RolesList,
  Q.RecentlyViewedRoles,
  Q.RoleDetail,
];

const ROLE_GROUPS = [
  ...ROLES,
  Q.GetRoleGroups,
  Q.RoleGroupDetail,
  Q.RoleGroupHeader_RolesCount,
  Q.RoleGroupHeader_PeopleCount,
  Q.RoleGroupRolesTable_Roles,
  Q.SubRoleGroupsList_RoleGroup,
  Q.DepartmentsPageContentRoleGroups,
];

const WORKFLOWS = [
  Q.WorkflowsTable,
  Q.WorkflowsPageHeader,
  Q.LibraryItemOverviewPathMembership,
];

const GUEST_FEEDBACK = [
  Q.GuestFeedbackRecommendations,
  Q.GuestFeedbackRecommendationDetail,
  Q.GuestFeedbackThemes,
  Q.GuestFeedbackThemeSummary,
];

const CERTIFICATES = [
  Q.BackfilledCertificates,
  Q.ComplianceRecords,
  Q.ComplianceSummary,
  Q.UserDetailComplianceCertificatesTabComplianceRecords,
  Q.SharpComplianceRecords,
];

const BRAND = [Q.Init, Q.BrandColor, Q.CertificateEndorser];

const PERMISSIONS = [Q.UserActionPermissionOverrides, Q.UserActionPermissions];

export const AGENT_MUTATION_REFETCH = {
  acceptGuestFeedbackRecommendation: GUEST_FEEDBACK,
  addUsersToLocations: [...PEOPLE, ...LOCATIONS],
  addUsersToRoles: [...PEOPLE, ...ROLES],
  applyBuilderActions: [...CONTENT, ...WORKFLOWS],
  archiveLibraryItems: CONTENT,
  archiveWorkflow: WORKFLOWS,
  assignLibraryItems: ASSIGNMENT_REFETCH_QUERIES,
  bulkPublishResources: CONTENT,
  bulkUpdateLibraryItemAutomations: LIBRARY_ACCESS,
  copyCourse: CONTENT,
  copyModule: CONTENT,
  createContentTagMembership: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  createCourse: CONTENT,
  createFolder: FOLDERS,
  createHrisEmployeeLink: [...PEOPLE, Q.HrisEmployees, Q.HrisEmployeeSidebar],
  createLibraryItemFolderMemberships: FOLDERS,
  createLocation: LOCATIONS,
  createLocationGroup: LOCATION_GROUPS,
  // Media shows once something uses it, and that write refetches.
  createMedia: [],
  createMessageBlast: [Q.MessageBlasts, Q.getMessageBlast],
  createModule: CONTENT,
  createRole: ROLES,
  createRoleGroup: ROLE_GROUPS,
  createSkill: CONTENT,
  createTag: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  createTask: [Q.TasksPage_Tasks, Q.SkillInstanceDialogV2_SkillInstance],
  createTrainingResourceV2: CONTENT,
  createUser: PEOPLE,
  createUserSharingPermissions: SHARING,
  createWorkflow: WORKFLOWS,
  deleteContentTagMembership: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  deleteLibraryItemFolderMemberships: FOLDERS,
  generateImage: [],
  // A read run as a mutation: the report preview's value counts.
  getReportFieldFrequencies: [],
  giveAccessToLibraryItems: LIBRARY_ACCESS,
  markAssignmentsComplete: ASSIGNMENT_REFETCH_QUERIES,
  publishCourse: CONTENT,
  publishModule: CONTENT,
  publishSkill: CONTENT,
  publishWorkflow: WORKFLOWS,
  reactivateEmployee: PEOPLE,
  reactivateEmployees: PEOPLE,
  rejectGuestFeedbackRecommendation: GUEST_FEEDBACK,
  removeAssignments: ASSIGNMENT_REFETCH_QUERIES,
  removeEmployee: PEOPLE,
  removeEmployees: PEOPLE,
  removeLibraryAccess: LIBRARY_ACCESS,
  removeUserSharingPermission: SHARING,
  removeUsersFromLocations: [...PEOPLE, ...LOCATIONS],
  removeUsersFromRoles: [...PEOPLE, ...ROLES],
  // A read run as a mutation: the custom report preview.
  runReport: [],
  saveReport: [Q.GetSavedReports, Q.GetReportViews],
  // Chats update live on their own.
  sendSeparateChatMessages: [],
  setEmployeeRoles: [...PEOPLE, ...ROLES],
  startTrainingResourceMediaToRichTextDocumentConversion: [
    Q.LibraryItemDetailPage_LibraryItem,
    Q.TrainingResourceDetailPage_CheckMediaConversionStatus,
  ],
  tagLibraryItems: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  unarchiveLibraryItems: CONTENT,
  unmarkAssignmentsComplete: ASSIGNMENT_REFETCH_QUERIES,
  untagLibraryItems: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  updateAutomation: [
    ...LIBRARY_ACCESS,
    Q.OrgPremiumContentConfigs,
    Q.UpcomingReassignmentCohortDetails,
  ],
  updateBackfilledCertificateExternalCertificateUrl: CERTIFICATES,
  updateCertificateEndorser: BRAND,
  updateClockInConfirmationSettings: [Q.ClockInConfirmationSettings],
  updateComplianceAttemptExternalCertificateUrl: CERTIFICATES,
  updateContentSharingPermission: SHARING,
  updateCourse: CONTENT,
  updateEmployeeCountSettings: [Q.GetEmployeeCountSettings],
  updateExpectedUsersConfigs: [Q.GetReportingTargetSet],
  updateFolder: FOLDERS,
  updateFolderPosition: FOLDERS,
  updateGuestFeedbackCorrectiveAction: GUEST_FEEDBACK,
  updateLocation: LOCATIONS,
  updateLocationGroup: LOCATION_GROUPS,
  updateModuleSettings: CONTENT,
  updateOrgBrandColor: BRAND,
  updateOrgLogo: BRAND,
  updateOrgSharpConfig: [Q.OrgSharpConfig],
  updateRole: [...ROLES, Q.PermissionCardEnabledCount],
  updateRoleGroup: ROLE_GROUPS,
  updateSkill: CONTENT,
  updateTag: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  updateTags: TAG_CONTEXT_STATE_REFETCH_QUERIES,
  updateTrainingResource: CONTENT,
  updateUser: PEOPLE,
  updateUserActionPermissionOverrides: PERMISSIONS,
  updateUserSharingPermission: SHARING,
  validateCourseForPublish: [],
  validateModuleForPublish: [],
  validateSkillForPublish: [],
  validateWorkflowForPublish: [],
} satisfies Partial<Record<McpMutationName, readonly QueryName[]>>;

const isMappedMutation = (
  name: string,
): name is keyof typeof AGENT_MUTATION_REFETCH =>
  Object.prototype.hasOwnProperty.call(AGENT_MUTATION_REFETCH, name);

/**
 * The queries to refetch for the tool calls in `events` that just
 * succeeded: the union of their mutations' lists, deduped. `seen` holds the
 * event keys already handled (a poll can return an event twice), and this
 * adds the new ones. A name this build doesn't know (a mutation added since
 * its schema snapshot) refetches nothing.
 */
export const agentWriteRefetchQueries = (
  turnId: string,
  events: readonly OpusAgentEventLike[],
  seen: Set<string>,
): string[] => {
  const queries = new Set<string>();
  for (const event of events) {
    const call = event.payload.toolCall;
    if (call?.status !== "SUCCEEDED" || !call.mutationNames?.length) continue;
    const key = `${turnId}:${event.index}`;
    if (seen.has(key)) continue;
    seen.add(key);
    for (const name of call.mutationNames) {
      if (!isMappedMutation(name)) continue;
      for (const query of AGENT_MUTATION_REFETCH[name]) queries.add(query);
    }
  }
  return [...queries];
};
